/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {Component, ComponentFactoryResolver, ComponentRef, OnInit, ViewChild, ViewContainerRef} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';

import {BehaviorSubject, EMPTY, Observable, of} from 'rxjs';
import {catchError, debounceTime, filter, finalize, switchMap, takeUntil, tap} from 'rxjs/operators';

import * as moment from 'moment/moment';

import {RepEntityUserManagement, TreeRoot} from '../../+organizations/org-interfaces';
import {OrgTreeService} from '../../+organizations/org-tree.service';
import {DATE_MAX, DATE_MIN} from '../../app.constants';
import {Destroyable} from '../../destroyable.component';
import {CliReg} from '../../models/models.interfaces';
import {strToDate} from '../../shared/utils/date';
import {Data} from '../api';
import {SimplifiedPaySerieDTO} from '../api/GaApi';
import {RolesService} from '../authentication/roles/roles.service';
import {FileChangesSessionService} from '../files/file-changes-session.service';
import {FileChangesService} from '../files/file-changes.service';
import {
  DefaultValue,
  FormCardFieldComponent
} from '../form-generator/form-card/form-card-field/form-card-field.component';
import {GroupZoneDescriptor, ZoneDescriptor, ZoneValues} from '../form-generator/form-generator.interfaces';
import {FormGeneratorUtils} from '../form-generator/form-generator.utils';
import {ModalService} from '../modal';
import {TreeNode} from '../ui';
import {DropdownTreeviewConfig} from '../ui/tree-multi-select/dropdown-treeview.component';
import {TreeItem} from '../ui/tree-multi-select/treeview.component';
import {extractValue} from '../utils/browser';
import {isPresent} from '../utils/lang';

@Component({
  selector: 'gp-modal-org-edit',
  providers: [FileChangesSessionService],
  styleUrls: ['./org-edit-modal.component.scss'],
  templateUrl: './org-edit-modal.component.html'
})
export class OrgEditModalComponent extends Destroyable implements OnInit {

  public extractValue = extractValue;
  public editedOrg: any;
  public userManagement: boolean = null;
  public selectedOrgName: string;
  public regulationForm: UntypedFormGroup;
  public regulationFields: any[] = [];
  public loadingConfidFields: boolean = false;
  public fromScratch = false;

  public parentsTree: Array<TreeNode<any>> = null;
  public parentsList: Array<any>;
  public isBeginBeforeEnd: boolean = true;
  public isParentRelationBeginDateInsideRange: boolean = true;
  public isParentRelationEndDateInsideRange: boolean = true;
  public isParentBeginBeforeEnd: boolean = true;
  public isAllowedtoCreate: boolean = null;
  public allRegulations: any[];
  public regulationTree: TreeItem[];
  public selectedTree: TreeRoot;
  public treeConfig: DropdownTreeviewConfig = {
    isShowFilter: false,
    isShowCollapseExpand: false,
  };
  public paySeries: SimplifiedPaySerieDTO[];
  public isOrgOtherIdTaken = false;

  private initialBeginDate;
  private initialEndDate;

  @ViewChild('fieldRowsContainer', {read: ViewContainerRef})
  public rowsContainer: ViewContainerRef;
  public submitData: Function;
  public saving: boolean = false;
  public selectOrgEditFormControl = this.formBuilder.control(null);
  public isPresent = isPresent;
  public orgIdentificationChangeSubject$ = new BehaviorSubject<string>('');
  public parentBeginDate = '1970-01-01';
  public parentEndDate = '3000-01-01';
  private mapIdByEntityLevel: Map<number, number> = new Map<number, number>();
  private initialOtherId = '';
  protected readonly strToDate = strToDate;

  constructor(private orgTreeService: OrgTreeService,
              private componentFactoryResolver: ComponentFactoryResolver,
              private modalService: ModalService,
              private formBuilder: UntypedFormBuilder,
              private fileChangesService: FileChangesService,
              private fileChanges: FileChangesSessionService,
              private rolesService: RolesService) {
    super();
  }

  public ngOnInit() {
    this.regulationForm = this.formBuilder.group({});
    this.editedOrg.mustGenerateName = this.allowGenerateName();
    this.selectOrgEditFormControl.valueChanges.pipe(
      tap((value) => {
        if (this.parentsList) {
          const selectedParent = this.parentsList.find(item => item.id === value);
          this.parentBeginDate = selectedParent?.beginDate || '1970-01-01';
          this.parentEndDate = selectedParent?.endDate || '3000-01-01';
          this.onChangeParentBeginDate(this.editedOrg.parentBeginDate);
          this.onChangeParentEndDate(this.editedOrg.parentEndDate);
        }
      }),
      takeUntil(this.componentDestroyed$)
    ).subscribe();

    const allowcliregArray: Set<string> = this.editedOrg.allowclireg
      ? new Set(this.editedOrg.allowclireg.split(','))
      : new Set<any>();
    this.orgTreeService.getFormDescriptor(this.editedOrg.tree, this.editedOrg.entityType)
      .then((descriptor: any[]) => {
        this.allRegulations = descriptor;
        this.editedOrg.opts.regulations = descriptor;
        this.getCliRegTree(this.editedOrg.opts.regulations, allowcliregArray);
        // this.getParentsTree();
      });

    if (!this.editedOrg.opts.isCopy) {
      this.getParentsTree();
    }

    if (this.editedOrg.opts.isCopy) {
      this.editedOrg.otherId = undefined;
    }

    this.initialBeginDate = this.editedOrg.begindate;
    this.initialEndDate = this.editedOrg.enddate;
    this.selectedOrgName = this.editedOrg.name;
    this.initialOtherId = this.editedOrg.otherId;

    if (this.isPayrollSetTree()) {
      if (this.rolesService.hasRole('USER_ROLES_ADMIN')) {
        // user management only for PayrollSet tree
        this.getUserManagement();
      }
      if (this.isConfigurator()) {
        this.fetchPaySeries();
      }
    }
    this.orgIdentificationChangeSubject$.asObservable().pipe(
      debounceTime(300),
      filter((newOtherId) => newOtherId !== '' && newOtherId !== this.initialOtherId),
      switchMap((newOtherId) => {
        this.isOrgOtherIdTaken = false;
        return this.orgTreeService.isRepEntityOtherIdTaken(newOtherId);
      }),
      tap((isOrgOtherIdTaken) => this.isOrgOtherIdTaken = isOrgOtherIdTaken),
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }

  private fetchPaySeries() {
    this.orgTreeService.getPaySeries()
      .pipe(tap((paySeries: SimplifiedPaySerieDTO[]) => {
        if (!paySeries.find(paySerie => paySerie.id === this.editedOrg.paySerieID)) {
          paySeries.push({
            id: this.editedOrg.paySerieID,
            transId: `${this.editedOrg.paySerieID}`
          });
        }
        this.paySeries = paySeries;
      }))
      .subscribe();
  }

  public isConfigurator(): boolean {
    return this.rolesService.hasRole('CFGT_ADMIN');
  }

  public isPayrollSetTree() {
    return this.selectedTree.name.includes('PayrollSet');
  }

  public getParentsTree() {
    this.orgTreeService.getParentsTree(this.editedOrg.tree)
      .subscribe((data) => {
        this.parentsList = data;
        const lookupMap: Map<number, TreeNode<any>> = new Map<number, TreeNode<any>>();
        const tree = [];

        data.forEach((item: Data<CliReg>) => {
          if (this.selectedTree?.supportLevel) {
            if (this.editedOrg.entityLevel !== item.entityLevel + 1) {
              item.userSelectable = false;
            }
          }
          this.mapIdByEntityLevel.set(item.id, item.entityLevel);

          if (item.id === this.editedOrg.id) {
            item.userSelectable = false;
          }

          const addInfo: string = item.inhabitants ? ` (${item.inhabitants})` : '';

          lookupMap.set(item.id, {
            text: `${item.name || item.nodeName}${addInfo}`,
            value: item.id < 0 ? -item.id : item.id,
            data: item,
            selectable: item.userSelectable !== false,
            visible: true,
            children: []
          });
        });

        for (const item of Array.from(lookupMap.values())) {
          const parent: TreeNode<CliReg> = lookupMap.get(item.data.parentId);
          if (parent) {
            parent.children.push(item);
          } else {
            tree.push(item);
          }

          if (this.editedOrg.parent === item.value) {
            this.editedOrg.parentLazyValue = item.text;
          }
        }

        // Prevent selecting a node located on the same branch, under the edited node, as its parent.
        // Otherwise a cycle would be created (e.g. A -> B -> C -> A).
        if (isPresent(this.editedOrg.id)) {
          const editedNode: TreeNode<any> = lookupMap.get(this.editedOrg.id);
          if (editedNode) {
            this.markDescendantsAsNotSelectable(editedNode);
          }
        }

        this.selectOrgEditFormControl.addValidators(Validators.required);
        this.parentsTree = tree;
        this.selectOrgEditFormControl.patchValue(this.editedOrg.parent);

      });
  }

  /**
   * Recursively marks every descendant of the given node as not selectable, so a node cannot be
   * assigned one of its own children/grandchildren as parent (which would create a cycle).
   */
  private markDescendantsAsNotSelectable(node: TreeNode<any>): void {
    (node.children || []).forEach((child: TreeNode<any>) => {
      child.selectable = false;
      if (child.data) {
        child.data.userSelectable = false;
      }
      this.markDescendantsAsNotSelectable(child);
    });
  }

  public getCliRegTree(cliRegTree: any, selectedItems?: Set<string>): void {
    this.regulationTree = [];
    const tree = [];
    const treeItems: Map<number, TreeItem> = new Map<number, TreeItem>();
    cliRegTree.forEach((item: Data<CliReg>) => {
      const treeItem: TreeItem = new TreeItem(item.nickName, item.cliRegId);
      if (selectedItems && selectedItems.has(item.cliRegId.toString())) {
        treeItem.checked = true;
      }

      Object.assign(treeItem, {
        data: item.formDescriptor,
        selectable: true,
        visible: true
      });
      treeItems.set(item.cliRegId, treeItem);
    });

    for (const item of Array.from(treeItems.values())) {
      tree.push(item);
    }

    if (tree.length) {
      this.regulationTree = tree.sort();
    }
  }

  public onAllowedRegulationSelected(selectedCliRegIds: number[]): void {
    if (selectedCliRegIds && selectedCliRegIds.length) {
      this.editedOrg.opts.regulations = this.allRegulations
        .filter(regulation => selectedCliRegIds.includes(regulation.cliRegId));
      this.editedOrg.allowclireg = selectedCliRegIds.join(',');
      if (this.editedOrg.cliReg &&
        !this.editedOrg.opts.regulations.some(regulation => regulation.cliRegId === this.editedOrg.cliReg)) {
        this.editedOrg.cliReg = null;
      }
    } else {
      this.editedOrg.opts.regulations = [];
      this.editedOrg.cliReg = null;
      this.editedOrg.allowclireg = null;
    }
  }



  public getConfidentialityFields() {
    this.clearAddInfoForm();

    if (!this.editedOrg || !this.editedOrg.opts.isNew
      || !this.editedOrg.allowclireg || this.editedOrg.allowclireg.length <= 0) {
      return;
    }

    this.loadingConfidFields = true;

    this.orgTreeService.getConfidentialityFields(
      this.editedOrg.allowclireg,
      this.editedOrg.tree,
      this.editedOrg.entityType
    )
      .pipe(
        finalize(() => {
          this.loadingConfidFields = false;

        })
      )
      .subscribe((data) => {
        this.clearAddInfoForm();

        this.isAllowedtoCreate = data !== null;

        if (!this.isAllowedtoCreate) {
          return;
        }

        const zoneDescriptors: any[] = [];

        Object.keys(data || {}).forEach((groupKey: string) => {
          Object.keys(data[groupKey].zoneDescriptors).forEach((zoneKey: string) => {
            zoneDescriptors.push(data[groupKey].zoneDescriptors[zoneKey]);
          });
        });

        const promisesOfLoadedObservables: Array<Promise<FormCardFieldComponent>> =
          zoneDescriptors.map((zoneDescriptor: ZoneDescriptor) => {
            zoneDescriptor._type = zoneDescriptor.type;
            zoneDescriptor.type = 'LIST';
            zoneDescriptor.isRequired = true;
            zoneDescriptor.metadatas.items.forEach((item: { display: string, value: string }) => {
              if (!isPresent(item.display)) {
                item.display = item.value;
              }
            });
            const fieldRef: ComponentRef<FormCardFieldComponent> = this.rowsContainer.createComponent(
              this.componentFactoryResolver.resolveComponentFactory(FormCardFieldComponent));

            const field: FormCardFieldComponent = fieldRef.instance;
            this.regulationFields.push(fieldRef);
            field.groupZoneDescriptor = <GroupZoneDescriptor>{zones: []};
            field.descriptor = zoneDescriptor;
            field.value = new DefaultValue();
            field.cardColumns = 1;
            field.readOnly = zoneDescriptor.readOnly;
            field.editMode = true;

            return field.loaded.toPromise();
          });

        Promise.all(promisesOfLoadedObservables).then(fields => {
          fields.forEach(item => {
            this.regulationForm.addControl(FormGeneratorUtils.sanitizeZoneName(item.descriptor.zoneID), item.control);
          });
        });
      });
  }

  public clearAddInfoForm(reset: boolean = false) {
    this.regulationFields.forEach(ref => {
      this.regulationForm.removeControl(FormGeneratorUtils.sanitizeZoneName(ref.instance.descriptor.zoneID));
      ref.destroy();
    });
    this.regulationFields = [];

    this.isAllowedtoCreate = null;
  }

  public isFormValid() {
    if (!this.editedOrg) {
      return;
    }

    const parentIsNotFilled = this.fromScratch && this.selectOrgEditFormControl.invalid;
    if (this.editedOrg.opts.isNew && (this.regulationForm.invalid || !this.isAllowedtoCreate || parentIsNotFilled)) {
      return false;
    }

    if (this.isOrgOtherIdTaken) {
      return false;
    }

    return (this.editedOrg.name || this.editedOrg.mustGenerateName)
      && this.isBeginBeforeEnd
      && this.isParentBeginBeforeEnd
      && this.isParentRelationBeginDateInsideRange
      && this.isParentRelationEndDateInsideRange
      && (this.editedOrg.opts.isCopy || (this.editedOrg.allowclireg && this.editedOrg.allowclireg.length > 0))
      && this.editedOrg.cliReg;
  }

  public isAllowedRegulationInvalid(): boolean {
    return !this.editedOrg?.opts?.isCopy
      && (!this.editedOrg?.allowclireg || this.editedOrg.allowclireg.length <= 0);
  }

  public isDefaultRegulationInvalid(): boolean {
    return !this.editedOrg?.opts?.isCopy && !this.editedOrg?.cliReg;
  }

  public onChangeDates(): void {
    if (moment(this.editedOrg.begindate).isValid() && moment(this.editedOrg.enddate).isValid()) {
      this.isBeginBeforeEnd = moment(this.editedOrg.begindate)
        .isSameOrBefore(this.editedOrg.enddate);
    }
  }

  public onChangeParentBeginDate(newBeginDate: string): void {
    if (moment(newBeginDate).isValid() && moment(this.editedOrg.parentEndDate).isValid()) {
      this.isParentBeginBeforeEnd = moment(newBeginDate).isSameOrBefore(this.editedOrg.parentEndDate);
      this.isParentRelationBeginDateInsideRange = this.editedOrg?.tree !== 'Positions'
        ? moment(newBeginDate).isBetween(this.parentBeginDate, this.parentEndDate, null, '[]')
        : true;
    }
  }

  public onChangeParentEndDate(newEndDate: string): void {
    if (moment(this.editedOrg.parentBeginDate).isValid() && moment(newEndDate).isValid()) {
      this.isParentBeginBeforeEnd = moment(this.editedOrg.parentBeginDate).isSameOrBefore(newEndDate);
      this.isParentRelationEndDateInsideRange = this.editedOrg?.tree !== 'Positions'
      ? moment(newEndDate).isBetween(this.parentBeginDate, this.parentEndDate, null, '[]')
      : true;
    }
  }

  public onChangeOfIdentification(event: string): void {
    const otherId = event.trim();
    if (otherId === '' || otherId === this.initialOtherId) {
      this.isOrgOtherIdTaken = false;
      return;
    }
    this.orgIdentificationChangeSubject$.next(otherId);
  }

  public confirmSaveIfNecessary(modal): void {
    modal.close();
    if (moment(this.editedOrg.begindate).isAfter(moment(this.initialBeginDate))
      || moment(this.editedOrg.enddate).isBefore(moment(this.initialEndDate))) {
      this.modalService.dialogFromData(
        'save_reduce_position_date'
      ).then((response: string) => {
        if (response === 'yes') {
          this.saveOrganisation(modal);
        } else {
          this.cancel(modal);
        }
      });
    } else {
      this.saveOrganisation(modal);
    }
  }

  public cancel(modal) {
    this.fileChangesService.discardPendingFileChanges(this.fileChanges).pipe(
      tap(() => {
        modal.close();
        this.clearAddInfoForm();
      }),
      catchError(() => EMPTY),
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }

  public saveOrganisation(modal: any): void {
    this.saving = true;
    this.fileChangesService.saveWithPendingFileChanges(this.fileChanges, this.submitData(this.computeData())).pipe(
      switchMap(() => this.createOrUpdateOrDeleteUserManagement()),
      tap(() => modal.close()),
      catchError(() => EMPTY),
      finalize(() => this.saving = false),
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }

  public allowGenerateName(): boolean {
    return this.selectedTree.allowGeneratedName && (this.editedOrg.opts.isNew || this.editedOrg.opts.isCopy);
  }

  public toggleGenerateName(): void {
    if (!this.allowGenerateName()) {
      return;
    }
    this.editedOrg.mustGenerateName = !this.editedOrg.mustGenerateName;
  }

  private computeData(): any {
    const data: any = {
      name: this.editedOrg.mustGenerateName ? null : this.editedOrg.name,
      mustGenerateName: this.editedOrg.mustGenerateName,
      tree: this.editedOrg.tree,
      entityLevel: this.editedOrg.entityLevel,
      entityType: this.editedOrg.entityType,
      organization: this.editedOrg.organization,
      parent: this.editedOrg.parent?.toString(),
      begindate: !isPresent(this.editedOrg.begindate) || this.editedOrg.begindate === ''
        ? DATE_MIN
        : this.editedOrg.begindate,
      enddate: !isPresent(this.editedOrg.enddate) || this.editedOrg.enddate === ''
        ? DATE_MAX
        : this.editedOrg.enddate,
      parentBeginDate: !isPresent(this.editedOrg.parentBeginDate) || this.editedOrg.parentBeginDate === ''
        ? DATE_MIN
        : this.editedOrg.parentBeginDate,
      parentEndDate: !isPresent(this.editedOrg.parentEndDate) || this.editedOrg.parentEndDate === ''
        ? DATE_MAX
        : this.editedOrg.parentEndDate,
      userSelectable: this.editedOrg.userSelectable,
      cliReg: this.editedOrg.cliReg,
      subTitle1: this.editedOrg.subTitle1,
      subTitle2: this.editedOrg.subTitle2,
      subTitle3: this.editedOrg.subTitle3,
      searchFlagColour: this.editedOrg.searchFlagColour,
      allowclireg: this.editedOrg.allowclireg,
      treeType: 'ORGANIZATION',
      paySerieID: this.editedOrg.paySerieID || this.editedOrg.paySerieId,
      otherId: this.editedOrg.otherId
    };

    if (this.editedOrg.opts.isNew) {
      const repDatas = {};
      const regulationData = this.regulationForm.getRawValue();

      this.regulationFields.forEach(ref => {
        const descriptor: ZoneDescriptor = ref.instance.descriptor;
        if (!repDatas[descriptor.zoneGroup]) {
          repDatas[descriptor.zoneGroup] = this.getGroupStructure(descriptor.zoneGroup, {});
        }
        const type: string = descriptor._type;
        let value: number | string | boolean = regulationData[FormGeneratorUtils.sanitizeZoneName(descriptor.zoneID)];
        switch (type) {
          case 'INTEGER':
          case 'ENTITY':
          case 'ENTITY_DATA':
          case 'ENTITY_PREFIXED':
          case 'DECIMAL':
          case 'TREE':
          case 'CURRENCY':
          case 'PEOPLE_TREE':
          case 'ACTIVE_EMPLOYEE':
          case 'FILE':
            value = +value;
            break;
          case 'BOOLEAN':
            value = value === 'true';
            break;
          default:
            break;
        }

        if (type !== 'LIST' && type !== 'TREE'
          && type !== 'ENTITY' && type !== 'ENTITY_DATA' && type !== 'ENTITY_PREFIXED'
          && type !== 'PEOPLE_TREE' && type !== 'ACTIVE_EMPLOYEE') {
          repDatas[descriptor.zoneGroup][0].values[descriptor.zoneID] = {type, value};
        } else {
          repDatas[descriptor.zoneGroup][0].values[descriptor.zoneID] = {
            type,
            value,
            transId: ref.instance.value.transId
          };
        }
      });

      data.repDatas = repDatas;
    }
    return data;
  }

  private getGroupStructure(groupName: string, values: any = {}): ZoneValues[] {
    return [{
      id: undefined,
      begindate: DATE_MIN,
      isDated: groupName === 'Establishment',
      enddate: DATE_MAX,
      groupName,
      values
    }];
  }

  private getUserManagement(): void {
    if (this.editedOrg.id) {
      this.orgTreeService.getUserManagement(this.editedOrg.id)
        .subscribe((userManagement) => {
          this.userManagement = !!userManagement;
        });
    }
  }

  private createOrUpdateOrDeleteUserManagement(): Observable<RepEntityUserManagement> {
    if (!this.editedOrg.id || this.userManagement === null) {
      return of(null);
    }
    if (this.userManagement === false) {
      return this.orgTreeService.deleteUserManagement(this.editedOrg.id);
    }
    return this.orgTreeService.createOrUpdateUserManagement(this.editedOrg.id, {
      daysToCreate: -180,
      daysToDeactivate: 0
    });
  }

  public onElementSelected($event: { id: number }) {
    this.editedOrg.parent = $event.id;
    this.editedOrg.entityLevel = this.mapIdByEntityLevel.get($event.id) + 1;
  }
}
